import { RadarIcon } from "lucide-react"
import { useBiMetrics } from "modules/backoffice/hooks/bi/useBiMetrics"
import { Loading } from "modules/shared/components/Loading"
import { useTranslation } from "react-i18next"
import { BiMetricsResponseDataSchema, DateRange, TimeDimension } from "schema"
import { z } from "zod"
import { MetricsSection } from "./MetricsSection"

type BackofficeBiDisplayProps = {
  data: z.infer<typeof BiMetricsResponseDataSchema> | undefined
  isPending: boolean
  range: DateRange
  timeDimension: TimeDimension
}

export const BackofficeBiDisplay = ({
  data,
  isPending,
  range,
  timeDimension,
}: BackofficeBiDisplayProps) => {
  const { t } = useTranslation()

  const metricGroups = useBiMetrics({ data, range, timeDimension })

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loading />
      </div>
    )
  }

  const hasData = [Object.values(metricGroups).some((group) => group.length > 0)]

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <RadarIcon className="size-10 text-muted-foreground mb-3" strokeWidth={1.5} />
        <h2 className="text-md text-foreground">{t("backoffice.bi.noData.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("backoffice.bi.noData.description")}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      <MetricsSection
        title={t("backoffice.bi.costs")}
        metricGroups={metricGroups.Money}
        isPending={isPending}
      />
      <MetricsSection
        title={t("backoffice.bi.counts")}
        metricGroups={metricGroups.Count}
        isPending={isPending}
      />
      <MetricsSection
        title={t("backoffice.bi.rates")}
        metricGroups={metricGroups.Rate}
        isPending={isPending}
      />
    </div>
  )
}
